import { ReactNode } from 'react'
import { classNames } from '@/util/tailwindHelpers'
import { MediaItem } from '@/api/resources/shared/media'
import { AssetAttributes } from '@/api/resources/shared/assetAttributes'
import { Rarity } from '@/api/resources/shared/rarity'
import { InventoryItemMedia } from '@/components/InventoryItem/InventoryItemMedia'
import FluidAssetMintNumber from '@/components/InventoryItem/FluidAssetMintNumber'
import ProductCardBrandIcon from './ProductCardBrandIcon'
import { cssFormattedSpacing } from '@/util/tailwindHelpers'
import {
  hasRedeemableMedia,
  isFullWidthProductMedia
} from '@/util/assetHelpers'
import RedeemableMedia from '../RedeemableMedia'
import { MEDIA_SIZE } from '@/constants'
import { CardLayout, CardOrientation } from '../ProductFluidContainer'

export const PRODUCT_CARD_MAX_SIZE = { width: 338, height: 450 }
export const PRODUCT_CARD_MIN_WIDTH = 150
export const PRODUCT_CARD_MIN_WIDTH_MOBILE = 150
export const PRODUCT_CARD_SMALL_LAYOUT_THRESHOLD = 200

export type AssetLike = {
  name?: string
  media?: MediaItem[]
  drop_name?: string
  attributes?: AssetAttributes
  mint_num?: number
  mint_count?: number
  redeemable?: boolean
  rarity?: Rarity
}

export interface ProductCardInterface {
  onClick?: () => void
  layout?: CardOrientation
  size?: CardLayout
  onLayoutChange?: (isSmallLayout: boolean) => void
  overlayChildren?: ReactNode
  isDetailPanelActive?: boolean
  isHovered?: boolean
  setIsHovered?: (hovered: boolean) => void
  infoSlot?: ReactNode
  pillSlot?: ReactNode
  className?: string
  mediaClasses?: string
  asset: AssetLike
  brandImage?: {
    id: string
    path: string
  }
  isCatalogAsset?: boolean
}

const ProductCardDetails = ({
  onClick,
  className = '',
  isDetailPanelActive = false,
  isHovered = false,
  setIsHovered = () => {},
  layout = 'vertical',
  size = 'default',
  asset,
  overlayChildren,
  infoSlot,
  mediaClasses,
  brandImage,
  isCatalogAsset = false
}: ProductCardInterface) => {
  const { attributes, media, mint_num, mint_count, rarity } = asset
  const isHorizontalLayout = layout === 'horizontal'
  const isSmallSize = size === 'small'

  const _rarity = rarity || attributes?.rarity

  const showRedeemableMedia = hasRedeemableMedia(asset)
  const isFullWidthAsset = isFullWidthProductMedia(asset)

  const itemMediaObjectFit = isFullWidthAsset ? 'cover' : 'contain'

  const borderRadius = isSmallSize
    ? `calc(var(--extrinsicScale) * ${cssFormattedSpacing(4)})`
    : '4em'

  const baseMediaClasses = isFullWidthAsset
    ? ''
    : classNames(
        'px-[3em] pt-[4em]',
        mediaWrapperClasses({
          size,
          layout,
          isCatalogAsset
        })
      )

  return (
    <div
      className={classNames(
        'flex flex-1 relative min-w-0 min-h-0 min-[570px]:aspect-[338/450] before:inset-[-1px] before:border-1.5 before:absolute before:content-[""] before:rounded-[var(--borderRadius)] before:z-[1] before:pointer-events-none before:border-defaultBorder',
        className,
        { 'before:border-blue--light': isDetailPanelActive }
      )}
      style={{
        '--borderRadius': borderRadius
      }}
      onMouseOver={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div
        className="flex flex-1 w-full overflow-hidden transform-gpu bg-gray-900"
        style={{
          borderRadius
        }}
      >
        <div
          className={classNames(
            'flex-1 relative h-full w-full flex',
            isHorizontalLayout ? 'min-[570px]:flex-col outline' : 'flex-col'
          )}
        >
          <div
            className={classNames(
              'relative flex min-h-0 justify-center',
              baseMediaClasses,
              mediaClasses
            )}
          >
            {showRedeemableMedia ? (
              <RedeemableMedia
                quality={{
                  token: MEDIA_SIZE.tiny,
                  box: MEDIA_SIZE.default
                }}
                media={asset.media}
              />
            ) : (
              <InventoryItemMedia
                objectFit={itemMediaObjectFit}
                media={media[0]}
                alt={asset.name}
              />
            )}

            {brandImage && (
              <ProductCardBrandIcon
                image={brandImage}
                size={isHorizontalLayout ? 'sm' : isSmallSize ? 'md' : 'lg'}
              />
            )}
          </div>

          <FluidAssetMintNumber
            mint_num={mint_num}
            mint_count={mint_count}
            shouldRevealMintCount={isHovered}
            rarity={_rarity}
            isSmallLayout={isSmallSize || isHorizontalLayout}
          />

          {infoSlot}
        </div>
        {onClick && (
          <button
            className={classNames('absolute inset-0 w-full', {
              'focus:outline-0 focus-visible:ring-[1.5px] ring-gray-500 ring-inset rounded-[inherit]':
                !isDetailPanelActive
            })}
            onClick={onClick}
          />
        )}
        {overlayChildren}
      </div>
    </div>
  )
}

const mediaWrapperClasses = ({
  size,
  layout,
  isCatalogAsset
}: {
  size: CardLayout
  layout: CardOrientation
  isCatalogAsset: boolean
}) => {
  const isHorizontal = layout === 'horizontal'
  const isSmall = size === 'small'

  let result = []

  if (isHorizontal) {
    result.push('px-[4em]')
  } else {
    if (isSmall) {
      result.push('px-[5em]')
    } else {
      result.push('px-[7em]')
    }
  }

  if (isCatalogAsset) {
    if (isSmall) {
      result.push('py-[2em]')
    } else {
      result.push('py-[3em]')
    }
  } else if (isSmall) {
    result.push('pt-[2em]')
  }

  return result
}

const ProductCard = ({ className, ...props }: ProductCardInterface) => {
  return (
    <div className={classNames('relative flex flex-1 h-full', className)}>
      <ProductCardDetails {...props} />
    </div>
  )
}

export default ProductCard
